import { useEffect, useState } from 'react'
import type { Edge, Node } from '@xyflow/react'
import { validateGraph } from '@/features/backtest/graph-engine/graph-validator'
import type { ValidationReport } from '@/features/backtest/graph-engine/graph-validator'
import { toEngineView } from '@/features/backtest/utils/engine-view'
import { connectedUniverseIds } from '@/features/backtest/utils/single-universe'
import { planEntryWindows } from '@/features/backtest/utils/entry-window'
import { singletonSchemas } from '@/features/backtest/schemas/compound'
import type { GraphNode, GraphEdge } from '@/features/backtest/schemas/backtest-types'
import type { NodeData } from '@/features/backtest/types/nodes'

/**
 * Graph-level run readiness — the editor-facing wrapper around the graph-engine
 * validator (graph-validator.ts). It answers "why can't I run this?" for the
 * LIVE editor graph, splitting the validator's two tiers: `errors` block a run,
 * `warnings` are backtestable-but-suspicious advice.
 *
 * The live editor graph carries nodes the pure validator doesn't understand:
 * subnet/group/sticky containers, subnet I/O pass-throughs, reroute dots, and
 * proxy edges that only exist while a subnet is collapsed. We validate the
 * engine view (`toEngineView`, the graph a run ships) with `inline`: pills
 * spliced out the way the engine does it, and nodes without a schema
 * (containers, annotations) dropped rather than misreported as unknown, so
 * they never wrongly block Run.
 */
export function computeGraphReadiness(nodes: Node<NodeData>[], edges: Edge[]): ValidationReport {
  const view = toEngineView(nodes, edges, { inline: true })
  const report = validateGraph(
    view.nodes as unknown as GraphNode[],
    view.edges as unknown as GraphEdge[],
  )

  // The engine graph names nodes the canvas does not have: a Trade Manager's
  // generated satellites, an entry window's Time Signal and Merge. Point each
  // issue at the node the user can see and fix.
  for (const issue of [...report.errors, ...report.warnings])
    if (issue.stepId) issue.stepId = view.ownerOf(issue.stepId)

  // On the RAW graph: one of each node its schema declares a singleton
  // (schemas/compound.ts). One Trade Manager places a strategy's trades, one
  // Report holds the run settings Run reads; a second leaves no answer to which.
  for (const { type, singleton } of singletonSchemas()) {
    const copies = nodes.filter((n) => n.type === type)
    if (copies.length < 2) continue
    report.errors.push({
      code: singleton.code,
      stepId: copies[1].id,
      message: `${copies.length} ${singleton.plural} are on the canvas; a strategy has one`,
      suggestion: `Delete all but one ${singleton.noun}`,
    })
  }

  // Editor-level rule, checked on the RAW graph: buildEngineStrategyGraph strips
  // universe nodes, so the engine validator above can never see them.
  //
  // A strategy runs against one sample. Several universes may sit on the canvas
  // (parking alternatives and rewiring between them is a good way to work), but
  // two wired in at once leaves no answer to "which sample did this run use?".
  // Connecting one normally releases the others; this catches the graphs that
  // arrive another way: an import, a paste, an AI-generated graph.
  const connected = connectedUniverseIds(nodes, edges)
  if (connected.length > 1) {
    report.errors.push({
      code: 'MULTIPLE_CONNECTED_UNIVERSES',
      stepId: connected[0],
      message: `${connected.length} Stock Selection nodes are connected; a strategy runs against one`,
      suggestion: 'Disconnect all but one Stock Selection node',
    })
  }

  // Also on the RAW graph: the payload has already dropped a window with no
  // trigger to gate, so the validator cannot tell the user it did nothing.
  for (const plan of planEntryWindows(nodes, edges)) {
    if (plan.trigger) continue
    report.warnings.push({
      code: 'ENTRY_WINDOW_NOTHING_TO_LIMIT',
      stepId: plan.entryId,
      message: 'The entry window has no true/false entry signal to limit',
      suggestion: 'Wire the entry signal into this node, or turn the window off',
    })
  }

  return report
}

/**
 * Debounced readiness for the editor. Recomputes ~300ms after the graph settles
 * so validation doesn't churn while the user is dragging or typing. The first
 * report is computed synchronously on mount (no empty-state flash); subsequent
 * graph changes wait out the debounce, and a change that lands inside the window
 * cancels the pending recompute.
 */
export function useGraphReadiness(
  nodes: Node<NodeData>[],
  edges: Edge[],
  debounceMs = 300,
): ValidationReport {
  const [report, setReport] = useState<ValidationReport>(() => computeGraphReadiness(nodes, edges))

  useEffect(() => {
    const handle = setTimeout(() => {
      setReport(computeGraphReadiness(nodes, edges))
    }, debounceMs)
    return () => clearTimeout(handle)
  }, [nodes, edges, debounceMs])

  return report
}
